import { isArray } from 'class-validator';
import { FormInstance } from 'antd/es/form';
import { apiInstance } from '../app/components/OnboardingForm/utils';
import {
    AUTH_API_URL,
    COMMENTS_API_URL,
    DOCUMENT_API_URL,
    ENTITIES_API_URL,
    FORM_API_URL,
    INVESTMENT_COMMITTEE_API_URL,
    RISK_COMMITTEE_API_URL,
    USER_API_URL,
} from '../constants/api';
import { debounce } from 'lodash';
import React from 'react';

import { IGNORE_FIELD_PREFIX } from '../app/components/Repeater';
import { Badge } from 'antd';

export const getStatus = (
    progress,
): 'success' | 'normal' | 'active' | 'exception' => {
    if (progress === 100) {
        return 'success';
    }

    if (progress === 0) {
        return 'normal';
    }
    return 'active';
};

export function clientHasOnlyEISProducts(form) {
    const eisValue = form.getFieldValue('eis');

    const hasEISProducts = isArray(eisValue) && eisValue.length > 0;
    const hasOtherProducts = [
        'deliverable-fx',
        'nondeliverable-fx',
        'precious-metals',
    ].some((fieldName) => {
        const fieldValue = form.getFieldValue(fieldName);

        return isArray(fieldValue) && fieldValue.length > 0;
    });

    return hasEISProducts && !hasOtherProducts;
}

export const getFieldsStatus = async (
    forms: FormInstance[],
    disableValidation = true,
) => {
    const invalidFields: any[] = [];
    const validFields = document.querySelectorAll('.ant-form-item-required');
    for await (const form of forms) {
        // @ts-ignore
        const formInternal = form.getInternalHooks('RC_FORM_INTERNAL_HOOKS');
        const fields = formInternal.getFields();
        try {
            await form.validateFields(fields.map((field) => field.name[0]));
        } catch (e: any) {
            e.errorFields
                .filter(
                    (field) => !field.name[0].startsWith(IGNORE_FIELD_PREFIX),
                )
                .map((error: string) =>
                    invalidFields.push({
                        error,
                        form: form.__INTERNAL__.name
                            ? form.__INTERNAL__.name
                            : 'background',
                    }),
                );
            if (disableValidation) {
                form.setFields(
                    e.errorFields.map(({ name }) => ({
                        name,
                        errors: [],
                    })),
                );
            }
        }
    }

    return {
        invalidFields,
        invalidNo: invalidFields.length,
        validNo: validFields.length,
    };
};

export const formService = async ({
    forms,
    getFields,
    formId,
    clientId,
    disableValidation = true,
    submit = false,
    attestationName = '',
}) => {
    const status = await getFieldsStatus(forms, disableValidation);

    const fieldsValues = getFields();

    const data = await apiInstance.post(
        `${FORM_API_URL}/${formId}/${clientId}`,
        JSON.stringify({
            fields: fieldsValues,
            submit,
            ino: status.invalidNo,
            vno: status.validNo,
            attestationName,
        }),
    );

    return {
        ...data,
        validation: status,
    };
};

export const entityService = async (clientId) =>
    apiInstance.get(`${ENTITIES_API_URL}/${clientId}`);

export const riskCommitteeService = async (clientId) =>
    apiInstance.get(`${RISK_COMMITTEE_API_URL}/${clientId}`);

export const createRiskCommitteeService = async (clientId, legalEntityId) =>
    apiInstance.post(`${RISK_COMMITTEE_API_URL}/${clientId}/${legalEntityId}`);

export const createInvestmentCommitteeService = async (
    clientId,
    legalEntityId,
) =>
    apiInstance.post(
        `${INVESTMENT_COMMITTEE_API_URL}/${clientId}/${legalEntityId}`,
    );

export const addCommentService = async (sectionId, parentId = '', data) =>
    apiInstance.post(`${COMMENTS_API_URL}/${sectionId}/${parentId}`, data);

export const riskCommitteeApproveService = async ({
    voteId,
    sectionId,
}: {
    voteId: string;
    sectionId: string;
}) =>
    apiInstance.post(`${RISK_COMMITTEE_API_URL}/${sectionId}/approve`, {
        voteId,
    });

export const resetVoteService = async ({ voteId, entryId, type }) =>
    apiInstance.post(
        `${
            type === 'rc'
                ? RISK_COMMITTEE_API_URL
                : INVESTMENT_COMMITTEE_API_URL
        }/${entryId}/resetVote`,
        {
            voteId,
        },
    );

export const riskCommitteeDisApproveService = async ({
    voteId,
    sectionId,
}: {
    voteId: string;
    sectionId: string;
}) =>
    apiInstance.post(`${RISK_COMMITTEE_API_URL}/${sectionId}/disapprove`, {
        voteId,
    });

export const investmentCommitteeApproveService = async ({
    voteId,
    sectionId,
}: {
    voteId: string;
    sectionId: string;
}) =>
    apiInstance.post(`${INVESTMENT_COMMITTEE_API_URL}/${sectionId}/approve`, {
        voteId,
    });

export const investmentCommitteeDisApproveService = async ({
    voteId,
    sectionId,
}: {
    voteId: string;
    sectionId: string;
}) =>
    apiInstance.post(
        `${INVESTMENT_COMMITTEE_API_URL}/${sectionId}/disapprove`,
        {
            voteId,
        },
    );

export const updateRCSectionValue = async (
    riskId,
    sectionName = '',
    data: { value: any } = { value: '' },
) =>
    apiInstance.post(
        `${RISK_COMMITTEE_API_URL}/${riskId}/${sectionName}Value`,
        data,
    );

export const updateICSectionValue = async (
    riskId,
    sectionName = '',
    data = { value: '' },
) =>
    apiInstance.post(
        `${INVESTMENT_COMMITTEE_API_URL}/${riskId}/${sectionName}Value`,
        data,
    );

export const updateOtcLimitsService = async (
    riskId,
    data = { otcDailySettlements: JSON.stringify([]) },
) => apiInstance.post(`${RISK_COMMITTEE_API_URL}/${riskId}/otc-limits`, data);

export const updateRCMflData = async (
    riskId: string,
    data: {
        mflEligibilityCriteria: string[];
        mflConcentrationLimits: string[];
    },
) => apiInstance.post(`${RISK_COMMITTEE_API_URL}/${riskId}/mflData`, data);

export const updateRCSectionStatus = async (
    riskId,
    sectionName = '',
    data = {},
) =>
    apiInstance.post(
        `${RISK_COMMITTEE_API_URL}/${riskId}/${sectionName}Status`,
        data,
    );

export const updateICSectionStatus = async (
    riskId,
    sectionName = '',
    data = { status: 0 },
) =>
    apiInstance.post(
        `${INVESTMENT_COMMITTEE_API_URL}/${riskId}/${sectionName}Status`,
        data,
    );

export const listInvestmentCommitteeCopySources = async () =>
    apiInstance.get(`${INVESTMENT_COMMITTEE_API_URL}/copy-sources`);

export const copyInvestmentCommitteeFromSource = async (
    targetId: string,
    sourceId: string,
) =>
    apiInstance.post(
        `${INVESTMENT_COMMITTEE_API_URL}/${targetId}/copy-from/${sourceId}`,
    );

export const riskCommitteeMembers = (id) => async () =>
    apiInstance.get(`${RISK_COMMITTEE_API_URL}/mentions/${id}`);

export const riskCommitteeVoters = (id) => async () =>
    apiInstance.get(`${RISK_COMMITTEE_API_URL}/voters/${id}`);

export const investmentCommitteeMembers = (id) => async () =>
    apiInstance.get(`${INVESTMENT_COMMITTEE_API_URL}/mentions/${id}`);

export const investmentCommitteeVoters = (id) => async () =>
    apiInstance.get(`${INVESTMENT_COMMITTEE_API_URL}/voters/${id}`);

export const updateDashboardOptions = async (data) => {
    return await apiInstance.post(`${USER_API_URL}/option/dashboard`, data);
};

export const setActiveCounterpartyService = async (id) => {
    return await apiInstance.get(
        `${AUTH_API_URL}/set-active-counterparty/${id}`,
    );
};

export const saveDocument = debounce(async (editor, _document, clientId) => {
    const statusIndicator = document.querySelector('#snippet-autosave-status');

    statusIndicator?.classList.add('busy');
    const trackChanges = await editor.plugins.get('TrackChanges');

    const commentsRepositoryPlugin = await editor.plugins.get(
        'CommentsRepository',
    );

    await apiInstance.post(`${DOCUMENT_API_URL}/${_document.id}/${clientId}`, {
        value: editor.getData(),
        suggestions: JSON.stringify(trackChanges.getSuggestions()),
        comments: JSON.stringify(commentsRepositoryPlugin.getCommentThreads()),
    });

    statusIndicator?.classList.remove('busy');

    return true;
}, 1000);

export const setChangedFields = debounce(async (setState, changedFields) => {
    return setState(!!changedFields.length);
}, 1000);

export const defaultValidateMessages = {
    /*eslint-disable no-template-curly-in-string*/
    default: "'${label}' is required",
    required: "'${label}' is required",
};

export const getTabValidationEl = (errors, isSubmitted, formName) => {
    const count = errors[formName];
    return count ? (
        <Badge count={count} offset={[-10, -8]} />
    ) : (
        <Badge
            count={0}
            showZero={true}
            offset={[-10, -8]}
            style={{ backgroundColor: '#52c41a' }}
        />
    );
};

export const stringToColour = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let colour = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff;
        colour += ('00' + value.toString(16)).substr(-2);
    }
    return colour;
};

export const getValue = (values, key, def?, mapper = false) => {
    const value = values.find((value) => value.name === key);

    if (value) {
        const obj = value.values[0];
        if (obj.type === 'text') {
            return obj.value || def;
        }

        if (obj.type === 'choiceGroup') {
            return obj.value || def;
        }

        if (obj.type === 'radioboxGroup') {
            return obj.value || def;
        }

        return obj.value || def;
    }
    return typeof def !== 'undefined' ? def : 'N/A';
};

export const getListValue = (values, key, separator = ',') =>
    getValue(values, key, []).join(separator);

export const CUSTOMER_RISK_PROFILE_VALUES = [
    { value: 'heightened', label: 'Heightened' },
    { value: 'not-heightened', label: 'Not Heightened' },
];
